<template>
  <div class="upload">
    <el-upload 
      :headers="headers" 
      :class="{ limitActive: imgUrlFlag && limit == 1, disabled: disabled }"
      :action="UPLOAD_BASE_URL + '/manager/upload'" 
      :list-type="listType" 
      name="file"
      accept=".jpg, .jpeg, .png, .gif, .JPG, .JPEG, .PNG, .GIF, .BMP"
      :on-success="handleUploadSuccess"
      :multiple="limit > 1" 
      ref="uploadShopImgRef" 
      :before-upload="handleBeforeUpload" 
      :file-list="fileList"
      :on-remove="handleUploadRemove" 
      :on-exceed="handleOnExceed" 
      :limit="limit" 
      :on-preview="handlePictureCardPreview"
      :disabled="disabled">
      <slot>
        <el-icon>
          <Plus />  <!-- 修复图标引用错误 -->
        </el-icon>
      </slot>
    </el-upload>

    <el-dialog v-model="dialogVisible">
      <img w-full style="width:100%;height: 100%;object-fit: contain;" :src="dialogImageUrl" alt="Preview Image" />
    </el-dialog>
  </div>
</template>

<script lang="js">
import { defineComponent, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { tips } from '@/utils/tips'
// 修复：引入正确的Plus图标（原代码引入Flag却用plus，导致图标显示异常）
import { Plus } from '@element-plus/icons-vue'
import { UPLOAD_BASE_URL } from '@/common/constant'

export default defineComponent({
  props: {
    fileList: {
      type: Array,
      default() {
        return []
      }
    },
    listType: {
      type: String,
      default: 'picture-card'
    },
    limit: {
      type: Number,
      default: 5
    },
    isAdd: {
      type: Boolean,
      default: false
    },
    // 新增：单文件最大大小（KB），默认50KB，支持父组件自定义
    maxSize: {
      type: Number,
      default: 50
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Plus  // 注册Plus图标组件
  },
  emits: ['handleUploadSuccess', 'handleUploadRemove', 'handleUploadEmpty', 'handleAddImgUploadRemove'],
  setup(props, { emit }) {
    const uploadShopImgRef = ref(null)
    const currentLimitFlag = ref(false)
    const imgUrlFlag = ref(false)

    // 上传成功回调
    const handleUploadSuccess = (res, fileList) => {
      imgUrlFlag.value = true
      emit('handleUploadSuccess', { ...res, ...fileList })
    }

    // 初始化CSRF请求头（增加判空，避免DOM不存在时报错）
    const headers = ref()
    const initPage = () => {
      try {
        const csrfMeta = document.querySelector("meta[name='_csrf']")
        const csrfHeaderMeta = document.querySelector("meta[name='_csrf_header']")
        if (csrfMeta && csrfHeaderMeta) {
          headers.value = {
            [csrfHeaderMeta.content]: csrfMeta.content
          }
        }
      } catch (error) {
        console.warn('CSRF元标签初始化失败：', error)
      }
    }
    initPage()

    // 清空文件列表（可选链避免组件未加载时报错）
    const emptyFileList = () => {
      uploadShopImgRef.value?.clearFiles()
    }

    // 超出文件数量限制提示
    const handleOnExceed = () => {
      tips({ code: 1, msg: `最多上传${props.limit}张图片!` })
    }

    // 新增：文件上传前校验（仅校验最大50KB，以KB为单位）
    const handleBeforeUpload = (file) => {
      // 计算文件大小：1KB = 1024B，file.size是字节数，除以1024转为KB
      const fileSizeKB = file.size / 1024
      
      // 1. 大小校验：超过maxSize（默认50KB）则阻止上传
      if (fileSizeKB > props.maxSize) {
        // 显示具体大小（四舍五入取整，提升用户体验）
        ElMessage.error(`文件太大！最大支持${props.maxSize}KB，当前文件${Math.round(fileSizeKB)}KB`);
        return false  // 阻止上传
      }

      // 2. 格式二次校验（避免绕过accept限制的异常情况）
      const fileExt = file.name.split('.').pop().toLowerCase()
      const allowedExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp']
      if (!allowedExts.includes(fileExt)) {
        ElMessage.error('仅支持.jpg、.jpeg、.png、.gif、.bmp格式文件');
        return false  // 阻止上传
      }

      return true  // 校验通过，允许上传
    }

    // 文件移除回调（保留原有逻辑，优化容错）
    const handleUploadRemove = (file, fileList) => {
      imgUrlFlag.value = false
      if (file.response != null) {
        emit('handleUploadRemove', file.response.data.fileName)
      } else {
        if (props.isAdd == true) {
          try {
            let url = file.response?.data?.fileName  // 可选链避免null报错
            emit('handleAddImgUploadRemove', url)
          } catch (error) {
            console.warn('新增场景移除文件异常：', error)
          }
        } else {
          imgUrlFlag.value = false
          if (props.limit == 1) {
            emptyFileList()
            emit('handleUploadEmpty')
            return
          }
          const arr = file.url?.split('/uploads')  // 可选链避免null报错
          if (arr?.length > 1) {
            const url = '/uploads' + arr[1]
            emit('handleUploadRemove', url)
          }
        }
      }
    }

    // 图片预览（优化图片显示，避免拉伸）
    const dialogImageUrl = ref('')
    const dialogVisible = ref(false)
    const handlePictureCardPreview = (uploadFile) => {
      dialogImageUrl.value = uploadFile.url
      dialogVisible.value = true
    }

    return {
      headers,
      handleUploadSuccess,
      uploadShopImgRef,
      emptyFileList,
      handleBeforeUpload,
      handleUploadRemove,
      handleOnExceed,
      currentLimitFlag,
      imgUrlFlag,
      UPLOAD_BASE_URL,
      handlePictureCardPreview,
      dialogImageUrl,
      dialogVisible,
      maxSize: props.maxSize  // 暴露maxSize给模板提示文字使用
    }
  }
})
</script>

<style scoped lang="less">
.upload {
  .disabled {
    &:deep(.el-upload--picture-card) {
      display: none !important;
    }
    &:deep(.el-upload-list__item-status-label) {
      display: none !important;
    }
    &:deep(.el-upload-list__item .el-icon--close) {
      display: none !important;
    }
  }

  .limitActive {
    &:deep(.el-upload--picture-card) {
      display: none !important;
    }
  }

  // 优化提示文字样式，提升可读性
  .upload-tip {
    line-height: 1.4;
    color: #666;  // 适配浅色背景，避免过淡
  }
}

// 优化预览弹窗图片样式，避免拉伸变形
:deep(.el-dialog__body) {
  padding: 20px !important;
  text-align: center;  // 图片居中
}
</style>